import { inject, Injectable } from '@angular/core';
import { MessageService } from 'primeng/api';
import { Observable } from 'rxjs';
import { notifyAndTrackError } from '../../common/error';
import { AppInsightsService } from '../app-insights.service';
import { InsectaServiceBase } from './insecta.base.service';
import { IOrderItem, IOrderItemAddRequest, IOrderItemUpdateRequest } from './insecta.models';

@Injectable({
  providedIn: 'root'
})
export class OrderItemService extends InsectaServiceBase {
  private readonly msgService = inject(MessageService);
  private readonly appInsights = inject(AppInsightsService);

  public addOrderItem(orderId: string, orderItem: IOrderItemAddRequest): Observable<string> {
    return this.post<string>(`/orders/${orderId}/Items`, `v1`, orderItem);
  }

  public getOrderItem(orderId: string, id: string): Observable<IOrderItem> {
    return this.get<IOrderItem>(`/orders/${orderId}/Items/${id}`, 'v1');
  }

  public updateOrderItem(orderId: string, orderItem: IOrderItemUpdateRequest): Observable<any> {
    return this.put<IOrderItemUpdateRequest>(`/orders/${orderId}/Items/${orderItem.id}`, `v1`, orderItem);
  }

  public deleteOrderItem(orderId: string, id: string): Observable<any> {
    return this.delete(`/orders/${orderId}/Items/${id}`, 'v1');
  }

  public getCatalogInformation(catalogId: string): Observable<any> {
    return this.get<any>(`/catalog/${catalogId}/orderInformation`, 'v1');
  }

  public getCatalogInformationParsed(catalogId: string): Observable<any> {
    return new Observable((observer) => {
      this.get<any>(`/catalog/${catalogId}/orderInformation`, 'v1').subscribe({
        next: (json) => {
          if (typeof Worker !== `undefined`) {
            const worker = new Worker(new URL(`./json-parser.worker.ts`, import.meta.url));
            worker.onmessage = ({ data }) => {
              if (data.success) {
                observer.next(data.data);
                observer.complete();
              } else {
                observer.error(data.error);
              }
              worker.terminate();
            };
            worker.onerror = (error) => {
              notifyAndTrackError(
                this.msgService,
                this.appInsights,
                error,
                { component: 'OrderItemService', action: 'ParseCatalogWorker' },
                (error as any)?.message
              );
              worker.terminate();
            };
            worker.postMessage(json);
          } else {
            try {
              const parsedJson = JSON.parse(json);
              observer.next(parsedJson);
              observer.complete();
            } catch (err) {
              observer.error(`Error processing on main thread: ${err}`);
            }
          }
        },
        error: (err) => {
          observer.error(err);
        }
      });
    });
  }
}
